import { useParams } from "@tanstack/react-router";
import type { SafetyPolicyType, SafetyVideoStatus } from "@unbox/api";
import { SAFETY_POLICY_LABELS } from "@unbox/api";
import { ChevronDown, Clock, MessageTextSquare01, ShieldZap } from "@untitledui/icons";
import { format } from "date-fns";
import type { ComponentType } from "react";
import { useState } from "react";
import { useMe } from "@/api/me";
import { useUser } from "@/api/user";
import { numFormatter } from "@/components/activities/activity-components";
import { DetailedVideoStatusBadge } from "@/components/assurance/badges/video-status-badge";
import { tierToSeverity } from "@/components/safety-config/risk-groups";
import { useGmvFormatter } from "@/hooks/use-gmv-formatter";
import { getActionTypeIcon, getLogActionLabel } from "@/lib/assurance/action-types";
import { cx } from "@/utils/cx";
import { formatActivityDayHeader } from "@/utils/date-grouping";
import { pluralize } from "@/utils/text";
import { RiskSeverityPill } from "../../components/RiskSeverityPill";

/** Who performed the action — drives the avatar shown against the history item. */
export type ActivityActor = "brand" | "creator" | "unbox" | "external";

export type ActivityItem = {
  id: string;
  actor: ActivityActor;
  /** Brand user behind a manual action — their name is shown before the time. */
  userId?: string;
  title: string;
  timestamp?: string | Date;
  icon?: ComponentType<{ className?: string; strokeWidth?: number }>;
  /** Creator profile picture, used when actor is "creator". */
  avatarUrl?: string | null;
  /** Optional message body shown as a preview and expandable under the card. */
  message?: string;
  /** Risks found in this detection pass, shown as an expandable list ("Risk detected"). */
  riskList?: { name: string; severity: "critical" | "high" | "medium" | "low" }[];
  /** Snapshot of the video itself, shown as an expandable card (GMV Max campaign events). */
  videoCard?: {
    title: string;
    thumbnail?: string | null;
    status?: SafetyVideoStatus;
    gmv?: number | null;
    views?: number | null;
    sales?: number | null;
  };
  /** When set, renders a plain "Wait N days" ring row instead of a card — no other fields apply. */
  waitDays?: number;
  /** When set (and `waitDays` isn't), renders the same plain ring row with this text instead. */
  plainText?: string;
};

type VideoDetailLike = {
  videoTitle: string;
  urlFirstFrame?: string | null;
  status?: SafetyVideoStatus;
  metrics?: { views: number; revenue: number; unitSales: number } | null;
  actions: {
    id: string;
    actionType: string;
    userId: string | null;
    createdAt: string | Date;
    safetyPolicyType?: SafetyPolicyType | null;
    notes?: string;
  }[];
  // Not actionRecords — flags are "risk detected" events, and ad campaign rows have their own
  // synced/removed timestamps, so both are mapped into activity items separately below.
  flags?: { id: string; riskTypeShort: string; riskTier: number; createdAt: string | Date }[];
  adCampaigns?: {
    campaignId: string;
    campaignName: string;
    syncedAt: string | Date;
    removedAt: string | Date | null;
  }[];
};

/** Actions that expand into the video's own card (Figma nodes 12465:99029, 12465:99033) —
 *  removal/campaign membership is a property of the video, not a point-in-time snapshot, so
 *  the card shows its current title/thumbnail/status/metrics rather than something recorded
 *  when the action ran. */
const VIDEO_CARD_ACTION_TYPES = ["video_removed", "add_to_ad_campaign", "remove_from_ad_campaign"];

/**
 * The enrolment action's own type says nothing about what will happen to the video, so it names
 * the policy that was started instead. Falls back to the generic label for actions written before
 * the policy was recorded against them (safetyPolicyType is null there).
 */
const actionTitle = (actionType: string, safetyPolicyType?: SafetyPolicyType | null): string => {
  if (actionType === "trigger_sequence" && safetyPolicyType) {
    return `Policy "${SAFETY_POLICY_LABELS[safetyPolicyType]}" triggered`;
  }
  return getLogActionLabel(actionType);
};

/** Gives the trigger_sequence item something to expand into — every Activity item is
 *  expected to be expandable. */
const actionMessage = (actionType: string, safetyPolicyType?: SafetyPolicyType | null): string | undefined => {
  if (actionType === "trigger_sequence" && safetyPolicyType) {
    return `The Policy "${SAFETY_POLICY_LABELS[safetyPolicyType]}" has been automatically triggered`;
  }
  return undefined;
};

/**
 * Maps a video detail response to history items.
 */
export const buildActivityItems = (detail: VideoDetailLike | undefined): ActivityItem[] => {
  const items: ActivityItem[] = [
    ...(detail?.actions ?? []).map((a) =>
      a.actionType === "contact_creator"
        ? {
            id: a.id,
            actor: a.userId ? ("brand" as const) : ("unbox" as const),
            userId: a.userId ?? undefined,
            title: "Message sent to creator",
            timestamp: a.createdAt,
            icon: MessageTextSquare01,
            message: a.notes,
          }
        : {
            id: a.id,
            actor: a.userId ? ("brand" as const) : ("unbox" as const),
            userId: a.userId ?? undefined,
            title: actionTitle(a.actionType, a.safetyPolicyType),
            timestamp: a.createdAt,
            icon: getActionTypeIcon(a.actionType),
            message: actionMessage(a.actionType, a.safetyPolicyType),
            videoCard:
              VIDEO_CARD_ACTION_TYPES.includes(a.actionType) && detail
                ? {
                    title: detail.videoTitle,
                    thumbnail: detail.urlFirstFrame,
                    status: detail.status,
                    gmv: detail.metrics?.revenue ?? null,
                    views: detail.metrics?.views ?? null,
                    sales: detail.metrics?.unitSales ?? null,
                  }
                : undefined,
          },
    ),
    // All flags come from a single detection pass, so they're shown as one "Risk detected"
    // item with the full list of risks found, rather than one item per flag.
    ...((detail?.flags?.length ?? 0) > 0
      ? [
          {
            id: "risk-detected",
            actor: "unbox" as const,
            title: "Risk detected",
            timestamp: (detail?.flags ?? [])
              .map((f) => f.createdAt)
              .reduce((earliest, ts) => (new Date(ts) < new Date(earliest) ? ts : earliest)),
            icon: ShieldZap,
            riskList: (detail?.flags ?? []).map((f) => ({
              name: f.riskTypeShort,
              severity: tierToSeverity(f.riskTier ?? 4),
            })),
          },
        ]
      : []),
    // TODO: add new field "createdAt" for a stable way of knowing when the action was
    // executed (UNB-2299) — until then, detail.adCampaigns' timestamps (syncedAt/removedAt)
    // have no stable field to know when the video was actually added to the campaign:
    // syncedAt is a rolling "last synced" heartbeat that gets overwritten on every pipeline
    // run, not a one-time "added" timestamp. Showing it as a dated historical event is
    // misleading (it always looks like it just happened), so these are filtered out of the
    // feed entirely for now.
  ];

  return items;
};

const groupByDay = (items: ActivityItem[]) => {
  const sorted = [...items].sort((a, b) => new Date(b.timestamp ?? 0).getTime() - new Date(a.timestamp ?? 0).getTime());
  const groups: { key: string; label: string; items: ActivityItem[] }[] = [];
  for (const item of sorted) {
    const date = new Date(item.timestamp ?? 0);
    const key = format(date, "yyyy-MM-dd");
    const last = groups[groups.length - 1];
    if (last?.key === key) last.items.push(item);
    else groups.push({ key, label: formatActivityDayHeader(date), items: [item] });
  }
  return groups;
};

const ActorAvatar = ({ actor, avatarUrl }: { actor: ActivityActor; avatarUrl?: string | null }) => {
  const { data: me } = useMe();
  const { merchantId } = useParams({ strict: false }) as { merchantId?: string };

  if (actor === "unbox") {
    return (
      <img
        src="/logomark.svg"
        alt="Unbox"
        className="w-8 h-8 rounded-md object-contain bg-white border border-border-secondary p-1.5 shrink-0"
      />
    );
  }

  if (actor === "brand") {
    const merchant =
      me?.merchants?.find((m) => m.id === merchantId) ??
      me?.workspaces?.flatMap((w) => w.merchants).find((m) => m.id === merchantId);
    if (merchant?.logoUrl) {
      return <img src={merchant.logoUrl} alt={merchant.name} className="w-8 h-8 rounded-md object-cover shrink-0" />;
    }
  }

  if (actor === "creator" && avatarUrl) {
    return <img src={avatarUrl} alt="Creator" className="w-8 h-8 rounded-full object-cover shrink-0" />;
  }

  // External actors (and anyone without an image) fall back to a grey circle
  return <div className="w-8 h-8 rounded-full bg-gray-200 shrink-0" />;
};

// Resolved per-item so manual brand actions show who performed them ("Sam @08:30am")
const ActivityMeta = ({ userId, timestamp }: { userId?: string; timestamp: string | Date }) => {
  const { data: user } = useUser(userId);
  const name = user ? [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email : undefined;
  return (
    <span className="whitespace-nowrap">
      {name ? `${name} ` : ""}@{format(new Date(timestamp), "hh:mmaaa")}
    </span>
  );
};

const ActivityMetric = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col items-start">
    <span className="text-xs text-quaternary">{label}</span>
    <span className="text-sm font-medium text-tertiary">{value}</span>
  </div>
);

const ActivityVideoCard = ({ videoCard }: { videoCard: NonNullable<ActivityItem["videoCard"]> }) => {
  const formatGmv = useGmvFormatter();
  return (
    <div className="flex items-stretch">
      <div
        className="w-[123px] shrink-0 rounded overflow-hidden"
        style={!videoCard.thumbnail ? { background: "linear-gradient(135deg, #FFD6A5, #FF8FA3)" } : undefined}
      >
        {videoCard.thumbnail && (
          <img src={videoCard.thumbnail} alt={videoCard.title} className="w-full h-[176px] object-cover" />
        )}
      </div>
      <div className="flex-1 min-w-0 flex flex-col border-none">
        <div className="flex flex-col gap-3 p-4">
          <p className="text-sm text-primary underline decoration-dotted truncate">{videoCard.title}</p>
          {videoCard.status && <DetailedVideoStatusBadge status={videoCard.status} showInfoIcon />}
        </div>
        <div className="flex justify-between border-t border-border-secondary pt-4 pb-3 px-6 whitespace-nowrap">
          <ActivityMetric label="GMV" value={formatGmv(videoCard.gmv)} />
          <ActivityMetric label="Views" value={videoCard.views != null ? numFormatter.format(videoCard.views) : "—"} />
          <ActivityMetric label="Sales" value={videoCard.sales != null ? numFormatter.format(videoCard.sales) : "—"} />
        </div>
      </div>
    </div>
  );
};

const ActivityRiskList = ({ riskList }: { riskList: NonNullable<ActivityItem["riskList"]> }) => (
  <div className="flex flex-col gap-2 w-full">
    <p className="text-sm text-primary m-0">Video identified for the following risks:</p>
    <div className="flex flex-col gap-1.5">
      {riskList.map((risk) => (
        <div key={risk.name} className="flex items-center gap-2">
          <p className="flex-1 min-w-0 text-sm text-primary m-0">{risk.name}</p>
          <RiskSeverityPill
            name={risk.severity.charAt(0).toUpperCase() + risk.severity.slice(1)}
            severity={risk.severity}
          />
        </div>
      ))}
    </div>
  </div>
);

export const ActivityRow = ({ item, showConnector }: { item: ActivityItem; showConnector: boolean }) => {
  const [expanded, setExpanded] = useState(false);
  const Icon = item.icon;
  const expandable = !!item.message || !!item.videoCard || !!item.riskList;

  if (item.waitDays != null || item.plainText != null) {
    return (
      <div className="flex items-center gap-3 py-2">
        <div className="flex items-center justify-center w-7 h-7 rounded-full border border-secondary bg-secondary_subtle text-tertiary shrink-0">
          <Clock className="w-3.5 h-3.5" />
        </div>
        <span className="text-sm font-medium text-tertiary">
          {item.waitDays != null ? `Wait ${pluralize(item.waitDays, "day", "days")}` : item.plainText}
        </span>
      </div>
    );
  }

  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center shrink-0">
        <ActorAvatar actor={item.actor} avatarUrl={item.avatarUrl} />
        {showConnector && <div className="w-px bg-gray-300 flex-1 min-h-[12px]" />}
      </div>
      <div className="flex-1 min-w-0 pb-3">
        <button
          type="button"
          disabled={!expandable}
          onClick={() => setExpanded((prev) => !prev)}
          className={cx(
            "w-full flex items-center gap-3 px-3 py-2 bg-white border-none text-left",
            expanded ? "rounded-t-lg" : "rounded-lg",
            expandable && "cursor-pointer hover:bg-secondary transition-colors",
          )}
        >
          {Icon && <Icon className="w-5 h-5 shrink-0" strokeWidth={1.5} />}
          <div className="flex-1 min-w-0 flex flex-col gap-0.5">
            <span className="text-[13px] font-medium text-primary leading-5">{item.title}</span>
            <div className="flex items-center gap-2 text-xs text-tertiary">
              {item.timestamp && <ActivityMeta userId={item.userId} timestamp={item.timestamp} />}
              {item.message && !expanded && <span className="truncate">{item.message}</span>}
            </div>
          </div>
          {expandable && (
            <ChevronDown
              className={cx("w-5 h-5 text-quaternary shrink-0 transition-transform", expanded && "rotate-180")}
            />
          )}
        </button>
        {expanded && (item.message || item.videoCard || item.riskList) && (
          <div className={cx("bg-white border-none rounded-b-lg", item.videoCard ? "pl-2 py-2 pr-0" : "px-4 py-2")}>
            {item.videoCard ? (
              <ActivityVideoCard videoCard={item.videoCard} />
            ) : item.riskList ? (
              <ActivityRiskList riskList={item.riskList} />
            ) : (
              <p className="text-sm text-primary whitespace-pre-wrap break-words m-0">{item.message}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

type ActivityFeedProps = {
  items: ActivityItem[];
};

export const ActivityFeed = ({ items }: ActivityFeedProps) => {
  if (items.length === 0) {
    return <p className="text-[13px] text-tertiary m-0">No history yet</p>;
  }

  return (
    <div className="flex flex-col">
      {groupByDay(items).map((group) => (
        <div key={group.key}>
          <div className="py-2 flex items-center gap-3">
            <p className="text-xs font-medium text-secondary whitespace-nowrap m-0">{group.label}</p>
            <div className="flex-1 h-px bg-border-secondary" />
          </div>
          {group.items.map((item, i) => (
            <ActivityRow key={item.id} item={item} showConnector={i < group.items.length - 1} />
          ))}
        </div>
      ))}
    </div>
  );
};
